import { useEffect, useRef, useState } from "react";
import testimonial1 from "../assets/testimonial1.png";
import testimonial2 from "../assets/testimonial2.png";
import testimonial3 from "../assets/testimonial3.png";
import "./container.css";
import "./Testimonials.css";

const TESTIMONIALS = [
  {
    tag: "Simple & Clear",
    quote:
      "I was juggling 4 different EMIs every month and constantly missing due dates. Xyz helped me combine everything into one payment it honestly reduced my stress overnight.",
    rating: "4.2",
    name: "Rohit Sharma",
    location: "Delhi",
    avatar: testimonial1,
  },
  {
    tag: "Smooth Experience",
    quote:
      "The process was surprisingly smooth. I checked my eligibility in minutes and got approval much faster than expected. No unnecessary paperwork.",
    rating: "4.2",
    name: "Priya Mehta",
    location: "Mumbai",
    avatar: testimonial2,
  },
  {
    tag: "Quick & Easy",
    quote:
      "My monthly outflow is now much more manageable. I can actually plan my expenses without worrying about multiple deductions hitting my account.",
    rating: "4.2",
    name: "Arjun Patel",
    location: "Bangalore",
    avatar: testimonial3,
  },
];

const COUNT = TESTIMONIALS.length;
const AUTO_ROTATE_MS = 6500;
const SLIDE_MS = 750;

// Five physical DOM slots in a ring: rank 2 is always the centered/active
// one, ranks 1 & 3 are the visible neighbours, ranks 0 & 4 are an
// always-off-screen buffer on each side. Sliding just decrements/increments
// every slot's rank by one (a normal animated transform change); whichever
// slot falls off the ring (rank -1 or rank 5) is instantly teleported to the
// opposite buffer position with `animate: false` for that one frame -- and
// because that reposition happens entirely inside the off-screen buffer
// zone, it's invisible. There's no "reset point" and no fixed-length loop,
// so it can run forever in either direction without ever blinking.
const mod = (n: number, m: number) => ((n % m) + m) % m;

type Slot = { id: number; rank: number; contentIndex: number; animate: boolean };

const initialSlots: Slot[] = [0, 1, 2, 3, 4].map((rank) => ({
  id: rank,
  rank,
  contentIndex: mod(rank - 2, COUNT),
  animate: true,
}));

const QuoteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="27" height="24" viewBox="0 0 27 24" fill="none">
    <path
      d="M0.377823 10.8597H2.7115C3.306 10.8597 3.72239 11.4469 3.52578 12.0079L0.0492125 21.9286C-0.147398 22.4897 0.26899 23.0768 0.863488 23.0768H4.26327C4.59997 23.0768 4.90592 22.8809 5.04686 22.5752L10.8972 9.88295C10.9494 9.76969 10.9764 9.64647 10.9764 9.52176V3.45131C10.9764 1.54521 9.43121 0 7.5251 0H3.82913C1.92303 0 0.377823 1.54521 0.377823 3.45131V10.8597Z"
      fill="#0E0C0E"
    />
    <path
      d="M15.5708 10.8597H17.9045C18.499 10.8597 18.9154 11.4469 18.7188 12.0079L15.2422 21.9286C15.0456 22.4897 15.462 23.0768 16.0565 23.0768H19.4563C19.793 23.0768 20.0989 22.8809 20.2399 22.5752L26.0902 9.88295C26.1424 9.76969 26.1694 9.64647 26.1694 9.52176V3.45131C26.1694 1.54521 24.6242 0 22.7181 0H19.0222C17.116 0 15.5708 1.54521 15.5708 3.45131V10.8597Z"
      fill="#0E0C0E"
    />
  </svg>
);

const TestimonialSlot = ({
  slot,
  visibleSlots,
  centerOffset,
}: {
  slot: Slot;
  visibleSlots: number;
  centerOffset: number;
}) => {
  const testimonial = TESTIMONIALS[slot.contentIndex];
  const isActive = slot.rank === 2;
  const slotWidthPct = 100 / visibleSlots;
  // translateX(%) is relative to THIS element's own width, which already
  // equals one slot-width -- so each ring step is simply a multiple of
  // 100%, not a fraction of the viewport width.
  const offsetPct = (slot.rank - 2 + centerOffset) * 100;

  return (
    <div
      className={`testimonial-slot ${isActive ? "testimonial-slot--active" : "testimonial-slot--side"}`}
      style={{
        width: `${slotWidthPct}%`,
        transform: `translateX(${offsetPct}%)`,
        transition: slot.animate ? `transform ${SLIDE_MS}ms ease` : "none",
      }}
    >
      <div className="testimonial-card">
        <span className="testimonial-tag">{testimonial.tag}</span>

        <p className="testimonial-quote">
          <span className="testimonial-quote-mark" aria-hidden="true">
            "
          </span>
          {testimonial.quote}
        </p>

        <div className="testimonial-rating">
          <span className="testimonial-stars" aria-hidden="true">
            ★★★★★
          </span>
          <span>{testimonial.rating}</span>
        </div>

        <div className="testimonial-person">
          <img src={testimonial.avatar} alt={testimonial.name} className="testimonial-avatar" />
          <div>
            <div className="testimonial-name">{testimonial.name}</div>
            <div className="testimonial-location">{testimonial.location}</div>
          </div>
        </div>

        {isActive && (
          <span className="testimonial-quote-icon" aria-hidden="true">
            <QuoteIcon />
          </span>
        )}
      </div>
    </div>
  );
};

const DESKTOP_QUERY = "(min-width: 768px)";

const Testimonials = () => {
  const [slots, setSlots] = useState<Slot[]>(initialSlots);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia(DESKTOP_QUERY).matches
  );
  const activeContentIndexRef = useRef(0);
  const reEnableTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_QUERY);
    const handleChange = () => setIsDesktop(mql.matches);
    handleChange();
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  const visibleSlots = isDesktop ? 3 : 1;
  const centerOffset = isDesktop ? 1 : 0;

  const step = (direction: 1 | -1) => {
    const newActiveContentIndex = mod(activeContentIndexRef.current + direction, COUNT);
    activeContentIndexRef.current = newActiveContentIndex;

    setSlots((prev) => {
      const fallingOffRank = direction === 1 ? 0 : 4;
      const recycledRank = direction === 1 ? 4 : 0;
      const recycledContentOffset = direction === 1 ? 2 : -2;

      return prev.map((slot) => {
        if (slot.rank === fallingOffRank) {
          return {
            ...slot,
            rank: recycledRank,
            contentIndex: mod(newActiveContentIndex + recycledContentOffset, COUNT),
            animate: false,
          };
        }
        return { ...slot, rank: slot.rank - direction, animate: true };
      });
    });

    // Re-enable the recycled slot's transition on the next frame so it
    // slides normally again once it re-enters the active rotation.
    clearTimeout(reEnableTimeoutRef.current);
    reEnableTimeoutRef.current = setTimeout(() => {
      setSlots((prev) => prev.map((slot) => ({ ...slot, animate: true })));
    }, 30);
  };

  const goNext = () => step(1);
  const goPrev = () => step(-1);

  const goToReal = (targetIndex: number) => {
    const diff = mod(targetIndex - activeContentIndexRef.current, COUNT);
    if (diff === 0) return;
    // Shortest direction to the requested testimonial.
    const direction: 1 | -1 = diff <= COUNT - diff ? 1 : -1;
    const steps = direction === 1 ? diff : COUNT - diff;
    for (let i = 0; i < steps; i++) {
      setTimeout(() => step(direction), i * (SLIDE_MS + 30));
    }
  };

  useEffect(() => {
    const timer = setInterval(goNext, AUTO_ROTATE_MS);
    return () => clearInterval(timer);
  }, []);

  const activeSlot = slots.find((slot) => slot.rank === 2);
  const activeContentIndex = activeSlot ? activeSlot.contentIndex : 0;

  return (
    <section className="testimonials-section">
      <div className="testimonials-heading-bar">
        <div className="container-8xl">
          <h2 className="testimonials-heading font-['Inter']">What Are They Saying About EMI Saathi?</h2>
        </div>
      </div>

      <div className="container-8xl">
        <div className="testimonials-viewport">
          {slots.map((slot) => (
            <TestimonialSlot
              key={slot.id}
              slot={slot}
              visibleSlots={visibleSlots}
              centerOffset={centerOffset}
            />
          ))}
        </div>

        <div className="testimonials-controls">
          <button className="testimonials-arrow" onClick={goPrev} aria-label="Previous testimonial">
            ‹
          </button>

          <div className="testimonials-dots">
            {TESTIMONIALS.map((testimonial, index) => (
              <button
                key={testimonial.name}
                className={`testimonials-dot ${index === activeContentIndex ? "testimonials-dot--active" : ""}`}
                onClick={() => goToReal(index)}
                aria-label={`Show testimonial ${index + 1}`}
              />
            ))}
          </div>

          <button className="testimonials-arrow" onClick={goNext} aria-label="Next testimonial">
            ›
          </button>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
